import { readA11y } from './a11y';

/**
 * Buzzes and sounds, for a phone that is face down on the table.
 *
 * Everything is synthesised — no audio files to load — and everything goes
 * through the player's own Display choices: vibration on or off, and sound for
 * nothing, for the moments that need you, or for everything.
 */

export type Cue =
  /** It is your go. */
  | 'turn'
  /** Something you sent arrived. */
  | 'sent'
  /** A second ticking away at the end of a timer. */
  | 'tick'
  /** A reveal turning over. */
  | 'reveal'
  /** Somebody's big moment. */
  | 'moment';

/** Which cues still sound when sound is set to alerts only. */
const ALERTS: readonly Cue[] = ['turn', 'moment'];

const PATTERNS: Record<Cue, number | number[]> = {
  turn: [30, 60, 30],
  sent: 12,
  tick: 8,
  reveal: 18,
  moment: [20, 40, 20, 40, 60],
};

/** Notes as [frequency, start offset in seconds]. */
const TUNES: Record<Cue, [number, number][]> = {
  turn: [
    [880, 0],
    [1320, 0.11],
  ],
  sent: [[1046, 0]],
  tick: [[660, 0]],
  reveal: [
    [523, 0],
    [784, 0.08],
  ],
  moment: [
    [523, 0],
    [659, 0.1],
    [784, 0.2],
    [1046, 0.32],
  ],
};

let audio: AudioContext | null = null;

function context(): AudioContext | null {
  try {
    if (audio && audio.state !== 'closed') return audio;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    audio = Ctor ? new Ctor() : null;
    return audio;
  } catch {
    return null;
  }
}

function play(cue: Cue): void {
  const ctx = context();
  if (!ctx) return;
  try {
    // A phone that has not been touched yet keeps audio suspended; this is a
    // no-op until it has.
    void ctx.resume();
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    const peak = cue === 'tick' ? 0.06 : 0.16;
    const length = cue === 'moment' ? 0.7 : 0.45;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(peak, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + length);
    for (const [freq, at] of TUNES[cue]) {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      osc.connect(gain);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + 0.2);
    }
  } catch {
    // Autoplay policies differ; a missed ding is not worth surfacing.
  }
}

/** Plays a cue, as far as this player's settings allow. */
export function cue(kind: Cue): void {
  const settings = readA11y();
  if (settings.haptics === 'on') {
    try {
      navigator.vibrate?.(PATTERNS[kind]);
    } catch {
      // Unsupported or blocked.
    }
  }
  if (settings.sound === 'all' || (settings.sound === 'alerts' && ALERTS.includes(kind))) {
    play(kind);
  }
}
